import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { EmailInput } from "./EmailInput";
import { FormError } from "./FormError";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle, ArrowLeft } from "lucide-react";
import { Link } from "@tanstack/react-router";

/**
 * Forgot Password Form Component
 *
 * Form to request a password reset email.
 */
export function ForgotPasswordForm() {
  const { resetPassword, loading } = useAuth();
  const { t } = useTranslation("auth");

  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | undefined>();
  const [success, setSuccess] = useState(false);

  const validateEmail = (): boolean => {
    if (!email) {
      setEmailError(t("validation.emailRequired"));
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError(t("validation.emailInvalid"));
      return false;
    }
    setEmailError(undefined);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateEmail()) return;

    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to send reset email. Please try again.";
      setError(message);
    }
  };

  if (success) {
    return (
      <div className="space-y-4">
        <Alert className="border-green-500/50 bg-green-500/10">
          <CheckCircle className="h-4 w-4 text-green-500" />
          <AlertDescription className="text-green-700 dark:text-green-400">
            {t("forgotPassword.success.description")}
          </AlertDescription>
        </Alert>

        <Link to="/login" className="inline-flex items-center text-sm text-primary hover:underline">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t("forgotPassword.backToLogin")}
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormError message={error} />

      <p className="text-sm text-muted-foreground">{t("forgotPassword.subtitle")}</p>

      <EmailInput
        label={t("forgotPassword.emailLabel")}
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          setEmailError(undefined);
        }}
        placeholder={t("forgotPassword.emailPlaceholder")}
        error={emailError}
        disabled={loading}
      />

      <Button type="submit" className="h-11 w-full" disabled={loading}>
        {loading ? (
          <>
            <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
            {t("forgotPassword.submitting")}
          </>
        ) : (
          t("forgotPassword.submit")
        )}
      </Button>

      <Link
        to="/login"
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        {t("forgotPassword.backToLogin")}
      </Link>
    </form>
  );
}

export default ForgotPasswordForm;
